import { Card, CircularProgress, IconButton } from "@material-ui/core";
import moment from "moment";
import printJS from "print-js";
import * as React from "react";
import { t } from "../../i18n/util";
import { THEME } from "../../theme/ThemeStore";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { downloadSecureLink } from "../util/helpers";
import { FilePreviewDialog } from "./FilePreviewDialog";
import { SectionHeaderSmall } from "./Primitives";

type ApplicantFileFieldProps = {
    url?: string;
    documentName: string;
    documentSize: number;
    image?: boolean;
    video?: boolean;
    onDelete?: () => void;
    downloadOnly?: boolean;
    uploadDate?: Date;
    notes?: string;
};

export const ApplicantFileField = ({
    url,
    documentName,
    documentSize,
    image,
    video,
    onDelete,
    downloadOnly,
    uploadDate,
    notes
}: ApplicantFileFieldProps) => {
    const [open, setOpen] = React.useState(false);
    const isMobile = useMobileMediaQuery();
    const [showNotes, setShowNotes] = React.useState(false);
    const [downloading, setDownloading] = React.useState(false);

    const handlePrint = () => {
        if (url && !video) {
            if (image) {
                printJS(url, "image");
            } else {
                printJS(url);
            }
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        await downloadSecureLink(url, documentName);
        setDownloading(false);
    };

    const getFileType = () => {
        if (image) {
            return "image/jpeg";
        }
        if (video) {
            return "video/mp4";
        }
        return "application/pdf";
    };

    return (
        <>
            <Card
                className="blackDalBorder"
                style={{
                    display: "flex",
                    height: isMobile || (notes && showNotes) ? "unset" : 76,
                    alignItems: "center",
                    padding: isMobile ? 25 : "25px 55px 25px 25px",
                    gap: isMobile ? 32 : 0,
                    flexDirection: isMobile ? "column" : "row"
                }}
            >
                <div
                    style={{
                        flex: isMobile ? undefined : 5,
                        display: "flex",
                        alignItems: "center",
                        width: isMobile ? "100%" : "unset",
                        justifyContent: isMobile ? "space-between" : "flex-start",
                        gap: 16
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flex: isMobile ? undefined : 4,
                            overflow: "hidden",
                            gap: 16
                        }}
                    >
                        <div>
                            <div style={{ display: "flex", gap: 16 }}>
                                <img src={THEME.Images.icFileBlack} alt="" />
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <SectionHeaderSmall
                                        style={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap"
                                        }}
                                        label={documentName}
                                    />
                                    {uploadDate && <p className="caption">{moment(uploadDate).format("DD.MM.YYYY")}</p>}
                                </div>
                                {notes && (
                                    <IconButton onClick={() => setShowNotes(!showNotes)}>
                                        {showNotes ? (
                                            <img src={THEME.Images.icChevronUp} alt="" />
                                        ) : (
                                            <img src={THEME.Images.icComment} alt="" />
                                        )}
                                    </IconButton>
                                )}
                            </div>
                            {notes && showNotes && (
                                <div style={{ minWidth: 40, marginTop: 16 }} className="caption">
                                    {notes}
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            flex: isMobile ? undefined : 1,
                            textAlign: "start",
                            minWidth: 40
                        }}
                        className="caption"
                    >
                        {`${Math.ceil(documentSize / 104857.6) / 10} ${t("screen.dataUpload.mb")}`}
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        flex: isMobile ? undefined : 2,
                        alignItems: "center",
                        width: isMobile ? "100%" : "unset",
                        justifyContent: "flex-end",
                        gap: 60,
                        color: THEME.customColors.primary
                    }}
                    className="caption"
                >
                    <div
                        style={downloadOnly ? { opacity: 0.2 } : { cursor: "pointer" }}
                        onClick={downloadOnly ? undefined : () => setOpen(true)}
                    >
                        {t("screen.applicantDocuments.preview")}
                    </div>
                    {!downloading && (
                        <img
                            src={THEME.Images.icDownload24x24}
                            alt=""
                            onClick={handleDownload}
                            style={{ cursor: "pointer" }}
                        />
                    )}
                    {downloading && <CircularProgress size={24} />}

                    {!isMobile && (
                        <img
                            src={THEME.Images.icPrint24x24}
                            alt=""
                            onClick={downloadOnly ? undefined : handlePrint}
                            style={video || downloadOnly ? { opacity: 0.2 } : { cursor: "pointer" }}
                        />
                    )}

                    <img
                        src={THEME.Images.icDelete}
                        alt=""
                        onClick={downloadOnly ? undefined : onDelete}
                        style={downloadOnly ? { opacity: 0.2 } : { cursor: "pointer" }}
                    />
                </div>
            </Card>
            <FilePreviewDialog url={url} type={getFileType()} isOpen={open} setIsOpen={setOpen} />
        </>
    );
};
